// Thin sales-season price series from Watts Official PriceDiscovery (crop leg).
// Prefer this over Databricks tracker_daily. Escalate to mirror only if Watts
// dailies are empty / insufficient for the discovery-window trend.

function pick(row, ...keys) {
  if (!row) return undefined;
  const lower = {};
  for (const [k, v] of Object.entries(row)) {
    lower[String(k).toLowerCase().replaceAll(" ", "_")] = v;
  }
  for (const k of keys) {
    const v = lower[String(k).toLowerCase().replaceAll(" ", "_")];
    if (v != null && v !== "") return v;
  }
  return undefined;
}

function numOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function asDayList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "object") return Object.values(raw);
  return [];
}

/**
 * @param {object|null} cropPd — Official PriceDiscovery row (input_cost_type=1)
 * @returns {{ ready: boolean, blockers: string[], source: string, series: object[], summary: object|null }}
 */
export function buildWattsSalesTracker(cropPd) {
  if (!cropPd) {
    return {
      ready: false,
      blockers: ["PriceDiscovery (crop) missing"],
      source: "watts",
      series: [],
      summary: null,
      needsMirror: false,
    };
  }

  const summary = pick(cropPd, "mcopricediscoverysummary", "mcoPriceDiscoverySummary") || {};
  const grouped = pick(cropPd, "mcopricediscoverygrouped", "mcoPriceDiscoveryGrouped") || {};
  const flat = pick(cropPd, "mcopricediscoverydetails", "mcoPriceDiscoveryDetails");

  let days = asDayList(pick(grouped, "projecteddetails", "projectedDetails"));
  if (!days.length) {
    days = asDayList(flat).filter((d) => {
      const t = String(pick(d, "pricetype", "priceType", "window", "leg") || "").toLowerCase();
      return !t || t.includes("proj");
    });
  }

  const series = days
    .map((d) => ({
      date: String(pick(d, "date", "tradedate", "tradeDate") || ""),
      price: numOrNull(pick(d, "price", "settlement", "settlementprice")),
      futuresSymbol: String(pick(d, "futuressymbol", "futuresSymbol", "symbol") || ""),
    }))
    .filter((d) => d.date && d.price != null)
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

  const projectedPrice = numOrNull(pick(summary, "projectedPrice", "projectedprice"));
  const harvestPrice = numOrNull(pick(summary, "harvestPrice", "harvestprice"));
  const windowStart = pick(summary, "projectedStartDate", "projectedstartdate");
  const windowEnd = pick(summary, "projectedEndDate", "projectedenddate");

  if (!series.length && projectedPrice == null) {
    return {
      ready: false,
      blockers: ["Live daily price series unavailable for this window"],
      source: "watts",
      series: [],
      summary: null,
      needsMirror: true,
    };
  }

  return {
    ready: true,
    blockers: series.length ? [] : ["Daily points empty — showing locked projected price only"],
    source: "watts",
    needsMirror: false,
    series,
    summary: {
      projectedPrice,
      harvestPrice,
      windowStart: windowStart ? String(windowStart) : null,
      windowEnd: windowEnd ? String(windowEnd) : null,
      dayCount: series.length,
      latestPrice: series.length ? series[series.length - 1].price : projectedPrice,
      firstPrice: series.length ? series[0].price : projectedPrice,
    },
  };
}
